/**
 * Module-level favorites store.
 *
 * Shared by every FavoriteToggle on the page. Logged-in users get one
 * GET per token and server-side persistence in their profile; anonymous
 * users get the same interface backed by localStorage (browser-only).
 * Read via useSyncExternalStore; no redux involvement.
 */

import { useSyncExternalStore } from 'react';

const LOCAL_KEY = 'techevent.favorites';

let state = { favorites: [], loadedFor: null };
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getState() {
  return state;
}

export function __resetForTests() {
  state = { favorites: [], loadedFor: null };
  listeners.clear();
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(LOCAL_KEY);
  }
}

function readLocal() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LOCAL_KEY));
    return Array.isArray(parsed)
      ? parsed.filter((uid) => typeof uid === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeLocal(favorites) {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(favorites));
  } catch {
    // Storage unavailable — anonymous favorites degrade to session-only.
  }
}

export async function initFavorites(token, apiPath) {
  if (!token) {
    if (state.loadedFor !== 'local') {
      state = { favorites: readLocal(), loadedFor: 'local' };
      emit();
    }
    return;
  }
  if (state.loadedFor === token) return;
  try {
    const response = await fetch(`${apiPath}/@favorites`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    if (response.ok) {
      const data = await response.json();
      state = { ...state, favorites: data.favorites ?? [], loadedFor: token };
      emit();
    }
  } catch {
    // Offline or server error — favorites stay empty for this session.
  }
}

export async function toggleFavorite(uid, token, apiPath) {
  const wasFavorite = state.favorites.includes(uid);
  const optimistic = wasFavorite
    ? state.favorites.filter((favorite) => favorite !== uid)
    : [...state.favorites, uid];
  if (!token) {
    writeLocal(optimistic);
    state = { ...state, favorites: optimistic };
    emit();
    return;
  }
  state = { ...state, favorites: optimistic };
  emit();
  try {
    const response = await fetch(`${apiPath}/@favorites`, {
      method: wasFavorite ? 'DELETE' : 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uid }),
    });
    if (!response.ok) throw new Error(`status ${response.status}`);
    const data = await response.json();
    state = { ...state, favorites: data.favorites ?? optimistic };
    emit();
  } catch {
    state = {
      ...state,
      favorites: wasFavorite
        ? [...state.favorites, uid]
        : state.favorites.filter((favorite) => favorite !== uid),
    };
    emit();
  }
}

export function useFavorites() {
  return useSyncExternalStore(subscribe, getState, getState);
}
